/**
 * Browser storage availability probes and in-memory fallback adapters.
 *
 * Mobile browsers with "block cookies" often disable localStorage,
 * sessionStorage, and IndexedDB together. Feature code must probe before
 * relying on persistence and degrade gracefully when unavailable.
 */

const PROBE_KEY = "__orf_storage_probe__";

/** True when localStorage read/write works in this context. */
export function isLocalStorageAvailable(): boolean {
  try {
    localStorage.setItem(PROBE_KEY, "1");
    localStorage.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Best-effort IndexedDB availability (API present — not a full open probe). */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}

export interface KeyValueStorage {
  getItem: (key: string) => string | null | Promise<string | null>;
  setItem: (key: string, value: string) => void | Promise<void>;
  removeItem: (key: string) => void | Promise<void>;
}

/** In-memory storage adapter for Supabase Auth when localStorage is blocked. */
export function createMemoryStorage(): KeyValueStorage {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

const RESTORE_TIMEOUT_MS = 3_000;

/** Race an async operation against a timeout — used for IndexedDB restore. */
export function withStorageTimeout<T>(
  promise: Promise<T>,
  ms: number = RESTORE_TIMEOUT_MS,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error("storage_timeout")), ms);
    }),
  ]);
}
